import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Sidebar } from '../../../components/sidebar';
import { Review, ReviewFilter, SortOption } from '../types';
import { mockReviews } from '../mocks/reviewsData';
import { EditProfileModal, type Profile } from '../../profiles-tracking/components/edit-profile-modal';
import { profilesTrackingService } from '../../profiles-tracking/services';
import { useEditProfileModal } from '../../../contexts/useEditProfileModal';
import { useUserStore, canEditSelectedProfile } from '../../../store/user';
import {
  ReviewsContainer,
  MainContent,
  Header,
  HeaderTop,
  HeaderTitle,
  Title,
  Subtitle,
  UpdateButton,
  TabsContainer,
  Tab,
  TabCount,
  FiltersBar,
  FilterButton,
  FilterIcon,
  SortSelect,
  ReviewsList,
  ReviewCard,
  ReviewHeader,
  ReviewAuthor,
  AuthorAvatar,
  AuthorInfo,
  AuthorName,
  RatingStars,
  ReviewDate,
  ReviewContent,
  ReviewText,
  ResponseSection,
  ResponseLabel,
  ResponseText,
  ResponseActions,
  ActionButton,
  TextArea,
  FiltersModal,
  FiltersModalOverlay,
  FiltersModalContent,
  FiltersModalHeader,
  FiltersModalTitle,
  CloseButton,
  FiltersModalBody,
  FilterGroup,
  FilterLabel,
  CheckboxGroup,
  CheckboxLabel,
  DateInput,
  FiltersModalFooter,
  ApplyButton,
  ClearFiltersButton,
} from './styles';

type TabType = 'all' | 'responded' | 'not-responded';

export const ReviewsPage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>(mockReviews);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [filters, setFilters] = useState<ReviewFilter>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('most-recent');
  const [editingResponseId, setEditingResponseId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const { isOpen: isEditModalOpen, closeModal } = useEditProfileModal();
  const [profileData, setProfileData] = useState<Profile | null>(null);
  const { user } = useUserStore();
  const canEdit = canEditSelectedProfile(user);

  // Inicializar perfil com dados do usuário
  useEffect(() => {
    if (user) {
      const userProfile: Profile = {
        id: user.businessProfiles?.[0] || "1",
        name: user.name || "",
        category: "Cliente",
        status: "active" as const,
        stats: {
          views: 0,
          clicks: 0,
          ranking: 0,
        },
      };
      setProfileData(userProfile);
    }
  }, [user]);

  // Respostas mockadas com IA
  const aiResponseTemplates = [
    "Olá {name}! Muito obrigado pela sua avaliação de {rating} estrelas! Ficamos muito felizes em saber que você teve uma experiência positiva conosco. Seu feedback é muito importante para continuarmos melhorando nossos serviços. Esperamos vê-lo(a) em breve!",
    "Prezado(a) {name}, agradecemos imensamente por dedicar seu tempo para avaliar nosso trabalho com {rating} estrelas. É um prazer saber que atendemos suas expectativas. Estamos sempre à disposição para servi-lo(a) da melhor forma possível!",
    "Oi {name}! Que alegria receber sua avaliação de {rating} estrelas! 🌟 Seu comentário nos motiva a continuar oferecendo o melhor atendimento. Conte sempre conosco e até a próxima!",
    "Olá {name}! Agradecemos demais pela sua confiança e pela avaliação de {rating} estrelas. É muito gratificante saber que nosso trabalho foi do seu agrado. Estamos sempre prontos para atendê-lo(a) com excelência!",
  ];

  const generateAIResponse = (review: Review) => {
    setGeneratingAI(true);
    
    // Simula delay de geração da IA
    setTimeout(() => {
      const template = aiResponseTemplates[Math.floor(Math.random() * aiResponseTemplates.length)];
      const firstName = review.authorName.split(' ')[0];
      const response = template
        .replace('{name}', firstName)
        .replace('{rating}', review.rating.toString());
      
      setResponseText(response);
      setGeneratingAI(false);
      toast.success('Resposta gerada com IA!');
    }, 1500);
  };

  // Calcular contadores das tabs
  const tabCounts = useMemo(() => {
    const total = reviews.length;
    const responded = reviews.filter(r => r.response).length;
    const notResponded = total - responded;

    return { total, responded, notResponded };
  }, [reviews]);

  // Filtrar reviews por tab
  const reviewsByTab = useMemo(() => {
    switch (activeTab) {
      case 'responded':
        return reviews.filter(r => r.response);
      case 'not-responded':
        return reviews.filter(r => !r.response);
      default:
        return reviews;
    }
  }, [reviews, activeTab]);

  // Aplicar filtros e ordenação
  const filteredAndSortedReviews = useMemo(() => {
    let filtered = [...reviewsByTab];

    // Aplicar filtros
    if (filters.rating && filters.rating.length > 0) {
      filtered = filtered.filter(r => filters.rating!.includes(r.rating));
    }

    if (filters.startDate) {
      filtered = filtered.filter(r => r.date >= filters.startDate!);
    }

    if (filters.endDate) {
      filtered = filtered.filter(r => r.date <= filters.endDate!);
    }

    // Aplicar ordenação
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'most-recent':
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case 'oldest':
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case 'highest-rating':
          return b.rating - a.rating;
        case 'lowest-rating':
          return a.rating - b.rating;
        default:
          return 0;
      }
    });

    return filtered;
  }, [reviewsByTab, filters, sortBy]);

  const handleRatingFilterChange = (rating: number, checked: boolean) => {
    setFilters(prev => {
      const currentRatings = prev.rating || [];
      const newRatings = checked
        ? [...currentRatings, rating]
        : currentRatings.filter(r => r !== rating);
      
      return { ...prev, rating: newRatings.length > 0 ? newRatings : undefined };
    });
  };

  const handleFilterChange = (key: keyof ReviewFilter, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const applyFilters = () => {
    setIsFilterModalOpen(false);
  };

  const handleSaveProfile = async (updatedProfile: Profile) => {
    try {
      await profilesTrackingService.updateProfile(
        updatedProfile.id,
        updatedProfile,
      );
      setProfileData(updatedProfile);
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar perfil');
      console.error(error);
      throw error;
    }
  };

  const handleReply = (reviewId: string) => {
    setReplyingToId(reviewId);
    setResponseText('');
  };

  const handleSaveResponse = (reviewId: string) => {
    if (!responseText.trim()) {
      toast.error('Digite uma resposta antes de enviar');
      return;
    }

    setReviews(prev => prev.map(review => {
      if (review.id === reviewId) {
        return {
          ...review,
          response: {
            id: `r${Date.now()}`,
            text: responseText,
            date: new Date().toISOString().split('T')[0],
            isEdited: false
          }
        };
      }
      return review;
    }));

    setReplyingToId(null);
    setResponseText('');
    toast.success('Resposta enviada com sucesso!');
  };

  const handleEditResponse = (reviewId: string) => {
    const review = reviews.find(r => r.id === reviewId);
    if (review?.response) {
      setEditingResponseId(reviewId);
      setResponseText(review.response.text);
    }
  };

  const handleUpdateResponse = (reviewId: string) => {
    if (!responseText.trim()) {
      toast.error('Digite uma resposta antes de salvar');
      return;
    }

    setReviews(prev => prev.map(review => {
      if (review.id === reviewId && review.response) {
        return {
          ...review,
          response: {
            ...review.response,
            text: responseText,
            isEdited: true
          }
        };
      }
      return review;
    }));

    setEditingResponseId(null);
    setResponseText('');
    toast.success('Resposta atualizada com sucesso!');
  };

  const handleDeleteResponse = (reviewId: string) => {
    if (window.confirm('Tem certeza que deseja apagar esta resposta?')) {
      setReviews(prev => prev.map(review => {
        if (review.id === reviewId) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { response, ...reviewWithoutResponse } = review;
          return reviewWithoutResponse;
        }
        return review;
      }));

      toast.success('Resposta apagada com sucesso!');
    }
  };

  const handleCancelEdit = () => {
    setEditingResponseId(null);
    setReplyingToId(null);
    setResponseText('');
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className="material-icons">
        {i < rating ? 'star' : 'star_border'}
      </span>
    ));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getAuthorInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <ReviewsContainer>
      <Sidebar />
      <MainContent>
        <Header>
          <HeaderTop>
            <HeaderTitle>
              <Title>Comentários e Avaliações</Title>
              <Subtitle>Gerencie e responda aos comentários recebidos de seus perfis</Subtitle>
            </HeaderTitle>
            <UpdateButton>
              <span className="material-icons">refresh</span>
              Atualizar
            </UpdateButton>
          </HeaderTop>

          <TabsContainer>
            <Tab 
              $active={activeTab === 'all'} 
              onClick={() => setActiveTab('all')}
            >
              Todas
              <TabCount>{tabCounts.total}</TabCount>
            </Tab>
            <Tab 
              $active={activeTab === 'responded'} 
              onClick={() => setActiveTab('responded')}
            >
              Respondidas
              <TabCount>{tabCounts.responded}</TabCount>
            </Tab>
            <Tab 
              $active={activeTab === 'not-responded'} 
              onClick={() => setActiveTab('not-responded')}
            >
              Não Respondidas
              <TabCount>{tabCounts.notResponded}</TabCount>
            </Tab>
          </TabsContainer>
        </Header>

        <FiltersBar>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <FilterButton onClick={() => setIsFilterModalOpen(true)}>
              <FilterIcon className="material-icons">filter_list</FilterIcon>
              Filtros
              {(filters.rating && filters.rating.length > 0 || filters.startDate || filters.endDate) && (
                <TabCount color="#1a73e8">
                  {(filters.rating?.length || 0) + (filters.startDate ? 1 : 0) + (filters.endDate ? 1 : 0)}
                </TabCount>
              )}
            </FilterButton>
          </div>

          <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)}>
            <option value="most-recent">Mais Recentes</option>
            <option value="oldest">Mais Antigas</option>
            <option value="highest-rating">Maior Classificação</option>
            <option value="lowest-rating">Menor Classificação</option>
          </SortSelect>
        </FiltersBar>

        <ReviewsList>
          {filteredAndSortedReviews.length === 0 ? (
            <div style={{ 
              padding: '60px 20px', 
              textAlign: 'center', 
              color: '#5f6368' 
            }}>
              <span className="material-icons" style={{ fontSize: '64px', color: '#dadce0', marginBottom: '16px' }}>
                inbox
              </span>
              <h3 style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
                Nenhum comentário encontrado
              </h3>
              <p style={{ fontSize: '14px' }}>
                Ajuste os filtros ou aguarde novos comentários
              </p>
            </div>
          ) : (
            filteredAndSortedReviews.map(review => (
              <ReviewCard key={review.id}>
                <ReviewHeader>
                  <ReviewAuthor>
                    <AuthorAvatar>
                      {getAuthorInitials(review.authorName)}
                    </AuthorAvatar>
                    <AuthorInfo>
                      <AuthorName>{review.authorName}</AuthorName>
                      <RatingStars>{renderStars(review.rating)}</RatingStars>
                    </AuthorInfo>
                  </ReviewAuthor>
                  <ReviewDate>{formatDate(review.date)}</ReviewDate>
                </ReviewHeader>

                <ReviewContent>
                  <ReviewText>{review.comment}</ReviewText>
                </ReviewContent>

                {canEdit && !review.response && replyingToId !== review.id && (
                  <ResponseActions>
                    <ActionButton variant="primary" onClick={() => handleReply(review.id)}>
                      <span className="material-icons">reply</span>
                      Responder
                    </ActionButton>
                  </ResponseActions>
                )}

                {replyingToId === review.id && (
                  <ResponseSection>
                    <ResponseLabel>
                      <span className="material-icons">edit</span>
                      Escrever Resposta
                    </ResponseLabel>
                    <TextArea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Digite sua resposta ao comentário..."
                      disabled={generatingAI}
                    />
                    <ResponseActions>
                      <ActionButton 
                        variant="ai" 
                        onClick={() => generateAIResponse(review)}
                        disabled={generatingAI}
                        type="button"
                      >
                        <span className="material-icons">auto_awesome</span>
                        {generatingAI ? 'Gerando...' : 'Gerar Resposta'}
                      </ActionButton>
                      <ActionButton variant="primary" onClick={() => handleSaveResponse(review.id)}>
                        <span className="material-icons">send</span>
                        Postar Resposta
                      </ActionButton>
                      <ActionButton onClick={handleCancelEdit}>
                        <span className="material-icons">close</span>
                        Cancelar
                      </ActionButton>
                    </ResponseActions>
                  </ResponseSection>
                )}

                {review.response && editingResponseId !== review.id && (
                  <ResponseSection>
                    <ResponseLabel>
                      <span className="material-icons">business</span>
                      Resposta do Proprietário
                      {review.response.isEdited && ' (editada)'}
                    </ResponseLabel>
                    <ResponseText>{review.response.text}</ResponseText>
                    <ResponseActions>
                      <ActionButton onClick={() => handleEditResponse(review.id)}>
                        <span className="material-icons">edit</span>
                        Editar
                      </ActionButton>
                      <ActionButton variant="danger" onClick={() => handleDeleteResponse(review.id)}>
                        <span className="material-icons">delete</span>
                        Apagar
                      </ActionButton>
                    </ResponseActions>
                  </ResponseSection>
                )}

                {editingResponseId === review.id && (
                  <ResponseSection>
                    <ResponseLabel>
                      <span className="material-icons">edit</span>
                      Editar Resposta
                    </ResponseLabel>
                    <TextArea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Digite sua resposta ao comentário..."
                      disabled={generatingAI}
                    />
                    <ResponseActions>
                      <ActionButton 
                        variant="ai" 
                        onClick={() => generateAIResponse(review)}
                        disabled={generatingAI}
                        type="button"
                      >
                        <span className="material-icons">auto_awesome</span>
                        {generatingAI ? 'Gerando...' : 'Gerar Resposta'}
                      </ActionButton>
                      <ActionButton variant="primary" onClick={() => handleUpdateResponse(review.id)}>
                        <span className="material-icons">save</span>
                        Salvar
                      </ActionButton>
                      <ActionButton onClick={handleCancelEdit}>
                        <span className="material-icons">close</span>
                        Cancelar
                      </ActionButton>
                    </ResponseActions>
                  </ResponseSection>
                )}
              </ReviewCard>
            ))
          )}
        </ReviewsList>

        {/* Modal de Filtros */}
        {isFilterModalOpen && (
          <FiltersModal $isOpen={isFilterModalOpen}>
            <FiltersModalOverlay onClick={() => setIsFilterModalOpen(false)}>
              <FiltersModalContent onClick={(e) => e.stopPropagation()}>
                <FiltersModalHeader>
                  <FiltersModalTitle>Filtros</FiltersModalTitle>
                  <CloseButton onClick={() => setIsFilterModalOpen(false)}>
                    <span className="material-icons">close</span>
                  </CloseButton>
                </FiltersModalHeader>

                <FiltersModalBody>
                  <FilterGroup>
                    <FilterLabel>Avaliação</FilterLabel>
                    <CheckboxGroup>
                      {[5, 4, 3, 2, 1].map(rating => (
                        <CheckboxLabel key={rating}>
                          <input
                            type="checkbox"
                            checked={filters.rating?.includes(rating) || false}
                            onChange={(e) => handleRatingFilterChange(rating, e.target.checked)}
                          />
                          <div style={{ display: 'flex', gap: '2px' }}>
                            {Array.from({ length: rating }, (_, i) => (
                              <span key={i} className="material-icons" style={{ fontSize: '16px', color: '#fbbc04' }}>
                                star
                              </span>
                            ))}
                          </div>
                        </CheckboxLabel>
                      ))}
                    </CheckboxGroup>
                  </FilterGroup>

                  <FilterGroup>
                    <FilterLabel>Data Inicial</FilterLabel>
                    <DateInput 
                      type="date"
                      value={filters.startDate || ''}
                      onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    />
                  </FilterGroup>

                  <FilterGroup>
                    <FilterLabel>Data Final</FilterLabel>
                    <DateInput 
                      type="date"
                      value={filters.endDate || ''}
                      onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    />
                  </FilterGroup>
                </FiltersModalBody>

                <FiltersModalFooter>
                  <ClearFiltersButton onClick={clearFilters}>
                    Limpar Filtros
                  </ClearFiltersButton>
                  <ApplyButton onClick={applyFilters}>
                    Aplicar
                  </ApplyButton>
                </FiltersModalFooter>
              </FiltersModalContent>
            </FiltersModalOverlay>
          </FiltersModal>
        )}
      </MainContent>

      {/* Modal de Edição de Perfil */}
      <EditProfileModal
        isOpen={isEditModalOpen}
        profile={profileData}
        onClose={closeModal}
        onSave={handleSaveProfile}
        hideIATab={true}
      />
    </ReviewsContainer>
  );
};

export default ReviewsPage;
